import React from 'react';

/**
 * Reusable Pagination Component
 * 
 * @param {number} currentPage - Current active page number
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback function when page changes (receives page number)
 * @param {string} className - Additional CSS classes
 */
const Pagination = ({ currentPage, totalPages, onPageChange, className = '' }) => {
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div className={`my-1 p-m-l col ${className}`}>
      <ul
        role="menubar"
        aria-disabled="false"
        aria-label="Pagination"
        className="pagination my-0 b-pagination justify-content-end"
      >
        {/* First Page Button */}
        <li
          role="presentation"
          aria-hidden={currentPage === 1 ? "true" : "false"}
          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
          style={{ borderTopLeftRadius: '0.25rem', borderBottomLeftRadius: '0.25rem' }}
        >
          {currentPage === 1 ? (
            <span
              role="menuitem"
              aria-label="Go to first page"
              aria-disabled="true"
              className="page-link"
              style={{ borderTopLeftRadius: '0.25rem', borderBottomLeftRadius: '0.25rem' }}
            >
              «
            </span>
          ) : (
            <button
              role="menuitem"
              type="button"
              aria-label="Go to first page"
              onClick={() => handlePageChange(1)}
              className="page-link"
              style={{ borderTopLeftRadius: '0.25rem', borderBottomLeftRadius: '0.25rem' }}
            >
              «
            </button>
          )}
        </li>

        {/* Previous Page Button */}
        <li
          role="presentation"
          aria-hidden={currentPage === 1 ? "true" : "false"}
          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
        >
          {currentPage === 1 ? (
            <span
              role="menuitem"
              aria-label="Go to previous page"
              aria-disabled="true"
              className="page-link"
            >
              ‹
            </span>
          ) : (
            <button
              role="menuitem"
              type="button"
              aria-label="Go to previous page"
              onClick={() => handlePageChange(currentPage - 1)}
              className="page-link"
            >
              ‹
            </button>
          )}
        </li>

        {/* Page Number Buttons */}
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <li
            key={page}
            role="presentation"
            className={`page-item ${currentPage === page ? "active" : ""}`}
          >
            <button
              role="menuitemradio"
              type="button"
              aria-label={`Go to page ${page}`}
              aria-checked={currentPage === page}
              aria-posinset={page}
              aria-setsize={totalPages}
              tabIndex={currentPage === page ? 0 : -1}
              onClick={() => handlePageChange(page)}
              className="page-link"
            >
              {page}
            </button>
          </li>
        ))}

        {/* Next Page Button */}
        <li
          role="presentation"
          aria-hidden={currentPage === totalPages ? "true" : "false"}
          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
        >
          {currentPage === totalPages ? (
            <span
              role="menuitem"
              aria-label="Go to next page"
              aria-disabled="true"
              className="page-link"
            >
              ›
            </span>
          ) : (
            <button
              role="menuitem"
              type="button"
              aria-label="Go to next page"
              onClick={() => handlePageChange(currentPage + 1)}
              className="page-link"
            >
              ›
            </button>
          )}
        </li>

        {/* Last Page Button */}
        <li
          role="presentation"
          aria-hidden={currentPage === totalPages ? "true" : "false"}
          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
          style={{ borderTopRightRadius: '0.25rem', borderBottomRightRadius: '0.25rem' }}
        >
          {currentPage === totalPages ? (
            <span
              role="menuitem"
              aria-label="Go to last page"
              aria-disabled="true"
              className="page-link"
              style={{ borderTopRightRadius: '0.25rem', borderBottomRightRadius: '0.25rem' }}
            >
              »
            </span>
          ) : (
            <button
              role="menuitem"
              type="button"
              aria-label="Go to last page"
              onClick={() => handlePageChange(totalPages)}
              className="page-link"
              style={{ borderTopRightRadius: '0.25rem', borderBottomRightRadius: '0.25rem' }}
            >
              »
            </button>
          )}
        </li>
      </ul>
    </div>
  );
};

export default Pagination;

